/**
 * Login-only validation popup for wrong email / wrong password / account inactive.
 * Isolated from global `showValidationMessage` so other modules are unchanged.
 */

import styles from '../styles/loginValidation.module.css'

/** Red shield + exclamation (password / email validation). */
const SHIELD_ALERT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>`

/** Amber user-off icon (Account Inactive). */
const USER_INACTIVE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" x2="22" y1="8" y2="13"/><line x1="22" x2="17" y1="8" y2="13"/></svg>`

let openLoginModalCount = 0

function lockBodyScroll() {
  openLoginModalCount += 1
  if (openLoginModalCount === 1) {
    document.body.style.overflow = 'hidden'
  }
}

function unlockBodyScroll() {
  openLoginModalCount = Math.max(0, openLoginModalCount - 1)
  if (openLoginModalCount === 0) {
    document.body.style.overflow = ''
  }
}

function formatLoginMessage(message: string): string {
  const trimmed = message.trim() || 'Please correct the highlighted fields.'
  return trimmed.replace(/([.!?])\s+(?=[A-Z])/g, '$1\n')
}

function isAccountInactiveTitle(title: string): boolean {
  return title.trim().toLowerCase() === 'account inactive'
}

/**
 * Allow only text, <br>, and mailto <a> — prevents XSS while supporting API HTML copy.
 */
function appendSafeLoginMessage(target: HTMLElement, raw: string): void {
  const host = document.createElement('div')
  host.innerHTML = raw

  const process = (node: Node, parent: HTMLElement) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode(node.textContent || ''))
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()

    if (tag === 'br') {
      parent.appendChild(document.createElement('br'))
      return
    }

    if (tag === 'a') {
      const href = (el.getAttribute('href') || '').trim()
      if (/^mailto:/i.test(href)) {
        const a = document.createElement('a')
        a.href = href
        a.className = styles.messageLink
        a.textContent = (el.textContent || '').trim() || 'Help Desk'
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        parent.appendChild(a)
      } else {
        parent.appendChild(document.createTextNode(el.textContent || ''))
      }
      return
    }

    Array.from(el.childNodes).forEach((child) => process(child, parent))
  }

  Array.from(host.childNodes).forEach((n) => process(n, target))
  if (!target.childNodes.length) {
    target.textContent = formatLoginMessage(raw)
  }
}

/**
 * Centered validation dialog used only by the login form.
 */
export function showLoginValidationMessage(
  message: string,
  title = 'Validation Message',
  onDismiss?: () => void,
): void {
  const existing = document.querySelectorAll('[data-login-validation-modal]')
  if (existing.length > 0) {
    openLoginModalCount = 0
    document.body.style.overflow = ''
    existing.forEach((n) => n.remove())
  }

  const inactive = isAccountInactiveTitle(title)
  lockBodyScroll()

  const backdrop = document.createElement('div')
  backdrop.setAttribute('data-login-validation-modal', 'true')
  backdrop.className = styles.overlay
  backdrop.setAttribute('role', 'presentation')

  const panel = document.createElement('div')
  panel.className = inactive
    ? `${styles.panel} ${styles.panelInactive}`
    : styles.panel
  panel.setAttribute('role', 'alertdialog')
  panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-labelledby', 'login-validation-modal-title')
  panel.onclick = (e) => e.stopPropagation()

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = inactive
    ? `${styles.closeButton} ${styles.closeButtonFloat}`
    : styles.closeButton
  closeBtn.setAttribute('aria-label', 'Close')
  closeBtn.innerHTML = '×'

  const header = document.createElement('div')
  header.className = inactive ? styles.headerInactive : styles.header

  const hero = document.createElement('span')
  hero.className = inactive ? styles.headerIconInactive : styles.headerIcon
  hero.setAttribute('aria-hidden', 'true')
  hero.innerHTML = inactive ? USER_INACTIVE_SVG : SHIELD_ALERT_SVG

  const heading = document.createElement('h3')
  heading.id = 'login-validation-modal-title'
  heading.className = inactive ? styles.titleInactive : styles.title
  heading.textContent = title

  if (inactive) {
    const accent = document.createElement('span')
    accent.className = styles.titleAccent
    accent.setAttribute('aria-hidden', 'true')

    header.appendChild(hero)
    header.appendChild(heading)
    header.appendChild(accent)
    panel.appendChild(closeBtn)
  } else {
    header.appendChild(hero)
    header.appendChild(heading)
    header.appendChild(closeBtn)
  }

  const body = document.createElement('div')
  body.className = inactive ? `${styles.body} ${styles.bodyInactive}` : styles.body

  const banner = document.createElement('div')
  banner.className = inactive
    ? `${styles.alertBanner} ${styles.alertBannerInactive}`
    : styles.alertBanner

  const alertIcon = document.createElement('span')
  alertIcon.className = inactive
    ? `${styles.alertIcon} ${styles.alertIconInactive}`
    : styles.alertIcon
  alertIcon.setAttribute('aria-hidden', 'true')
  alertIcon.textContent = '!'

  const text = document.createElement('div')
  text.className = inactive
    ? `${styles.message} ${styles.messageInactive}`
    : styles.message

  const raw = message.trim() || 'Please correct the highlighted fields.'
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(raw)
  if (looksLikeHtml || inactive) {
    appendSafeLoginMessage(text, raw)
  } else {
    text.textContent = formatLoginMessage(raw)
  }

  banner.appendChild(alertIcon)
  banner.appendChild(text)
  body.appendChild(banner)

  const footer = document.createElement('div')
  footer.className = inactive
    ? `${styles.footer} ${styles.footerInactive}`
    : styles.footer

  const okBtn = document.createElement('button')
  okBtn.type = 'button'
  okBtn.className = inactive
    ? `${styles.okButton} ${styles.okButtonWide}`
    : styles.okButton
  okBtn.textContent = 'Ok'

  footer.appendChild(okBtn)

  let dismissed = false
  const dismiss = () => {
    if (dismissed) return
    dismissed = true
    document.removeEventListener('keydown', onEscape)
    if (backdrop.parentNode) backdrop.remove()
    unlockBodyScroll()
    onDismiss?.()
  }

  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      dismiss()
    }
  }
  document.addEventListener('keydown', onEscape)

  closeBtn.onclick = () => dismiss()
  okBtn.onclick = () => dismiss()
  backdrop.onclick = (e) => {
    if (e.target === backdrop) dismiss()
  }

  panel.appendChild(header)
  panel.appendChild(body)
  panel.appendChild(footer)
  backdrop.appendChild(panel)
  document.body.appendChild(backdrop)

  okBtn.focus()
}
